import {Injectable, NgZone} from '@angular/core';
import {AlertController, ModalController} from '@ionic/angular';
import {AuthenticationService} from './authentication.service';
import {SpedizioneManualeDeactivateService} from '../spedizione-manuale/spedizione-manuale-deactivate.service';

// Logout automatico per inattività (issue #3203).
//
// Enforcement lato client: un unico setInterval da 1 s (fuori zona Angular) confronta now()
// con il timestamp di ultima attività condiviso in localStorage (multi-tab gratis, robusto a
// sleep/resume). Negli ultimi ~60 s mostra un alert Ionic con countdown ("Resta connesso");
// allo scadere revoca il refresh token lato server (best effort), esegue il logout locale e
// forza il redirect a login. La durata dei JWT non è toccata.
//
// Il setInterval vive nel browser: a browser chiuso non scatta nulla e la sessione resta in
// localStorage. Alla riapertura è expireRestoredSession(), chiamata all'avvio dell'app, a
// confrontare il timestamp di ultima attività (che nel frattempo è invecchiato) con il timeout.
@Injectable({providedIn: 'root'})
export class InactivityLogoutService {
  // Timestamp di ultima attività condiviso tra le tab della stessa app.
  private readonly ACTIVITY_KEY = 'archiconnect-v2-lastActivity';
  // Eventi di attività "tipica" dell'utente (mouse/tastiera/tocco/scroll).
  private readonly ACTIVITY_EVENTS = ['mousedown', 'mousemove', 'keydown', 'touchstart', 'scroll', 'wheel'];
  private readonly WARNING_MS = 60_000; // finestra di preavviso
  private readonly THROTTLE_MS = 1_000; // scrittura del timestamp al più una volta al secondo
  private readonly TICK_MS = 1_000;     // periodo del ciclo di verifica
  private readonly REVOKE_TIMEOUT_MS = 5_000; // attesa massima della revoca prima del logout locale

  private timeoutMs = 0;
  private warnAtMs = 0;
  private intervalId?: ReturnType<typeof setInterval>;
  private alert?: HTMLIonAlertElement;
  private warningPending = false;
  private loggingOut = false;
  private lastWrite = 0;

  constructor(
    private ngZone: NgZone,
    private authService: AuthenticationService,
    private alertController: AlertController,
    private modalController: ModalController,
    private spedizioneManualeDeactivate: SpedizioneManualeDeactivateService) {}

  // Avvia il tracking. ≤ 0 / assente = feature disattivata. Idempotente: se già attivo con lo stesso timeout non fa nulla (il refresh token periodico ri-emette currentUser: senza idempotenza il timer verrebbe azzerato a ogni refresh → nessuna disconnessione, FR-011).
  start(timeoutMinutes?: number): void {
    const minutes = timeoutMinutes ?? 0;
    if (minutes <= 0) {
      this.stop();
      return;
    }
    const timeoutMs = minutes * 60_000;
    if (this.intervalId && this.timeoutMs === timeoutMs) {
      return;
    }
    this.stop();
    this.timeoutMs = timeoutMs;
    this.warnAtMs = Math.max(timeoutMs - this.WARNING_MS, timeoutMs / 2);
    // Baseline all'avvio (login): riazzera il conteggio anche se resta un timestamp stale da una
    // sessione precedente sullo stesso tab (la chiave non viene cancellata al logout — vedi E4).
    this.touch(true);
    this.registerListeners();
    this.ngZone.runOutsideAngular(() => {
      this.intervalId = setInterval(() => this.tick(), this.TICK_MS);
    });
  }

  // Sessione ripristinata da localStorage al caricamento della pagina, dopo che l'app è rimasta
  // chiusa (o comunque ferma) più a lungo del timeout. Se è così la chiude e ritorna true.
  //
  // Senza questo controllo chi chiudeva il browser senza fare logout rientrava anche dopo giorni:
  // nessun timer gira a browser chiuso, e start() riazzera il conteggio (baseline) a ogni avvio,
  // quindi il timestamp invecchiato veniva semplicemente sovrascritto.
  //
  // ⚠️ Va chiamato SOLO all'avvio dell'app, prima di rinnovare il token e prima di start(): lì una
  // sessione presente è per definizione ripristinata. NON spostarlo dentro start(): gira anche
  // subito dopo un login, e la chiave lastActivity sopravvive al logout (E4), quindi un login a
  // distanza di giorni verrebbe disconnesso all'istante.
  //
  // Il logout locale è sincrono e precede la revoca: nessun frame con la sessione scaduta, nessuna
  // connessione real-time aperta col suo token, nessuna corsa con il rinnovo del refresh token (che
  // non parte, perché currentUser è già azzerato). La revoca server-side segue in background con
  // le credenziali catturate prima: è best effort come in autoLogout (FR-008).
  // Nessun forceDeactivate(): a pagina appena caricata non c'è alcuna risorsa attiva da spegnere.
  expireRestoredSession(timeoutMinutes?: number): boolean {
    const minutes = timeoutMinutes ?? 0;
    const user = this.authService.currentUserValue;
    if (minutes <= 0 || !user?.jwtToken) {
      return false;
    }
    const lastActivity = this.storedLastActivity();
    if (lastActivity === null || this.now() - lastActivity < minutes * 60_000) {
      return false;
    }
    const {jwtToken, refreshToken} = user;
    this.authService.logout();
    this.authService.revokeToken({jwtToken, refreshToken})
      .catch(() => {
        // best effort: la sessione locale è già chiusa (FR-008).
      });
    // Redirect esplicito: la dashboard ('') non ha AuthGuard, e senza questo resterebbe visibile.
    // ⚠️ Non sulle pagine dell'area account: conferma email, reset password, callback SSO e
    // registrazione sono pubbliche e portano il token nell'URL. Una sessione vecchia in storage non
    // deve far perdere quel link: chi lo apre non ha bisogno di login, e quelle pagine non guardano
    // la sessione (login compreso, che è già dove si vuole arrivare).
    if (!window.location.hash.startsWith('#/account/')) {
      window.location.hash = '#/account/login';
    }
    return true;
  }

  // Ferma il tracking e ripulisce lo stato interno. NON cancella la chiave lastActivity: è un timestamp innocuo e condiviso tra tab; cancellarla romperebbe i tab ancora loggati (E4).
  stop(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = undefined;
    }
    this.removeListeners();
    this.dismissWarning();
    this.timeoutMs = 0;
    this.warnAtMs = 0;
    this.loggingOut = false;
  }

  // Ciclo di verifica (pubblico per i test). Confronta il tempo trascorso con le soglie.
  tick(): void {
    if (this.loggingOut || this.timeoutMs <= 0) {
      return;
    }
    const elapsed = this.now() - this.lastActivity();
    if (elapsed >= this.timeoutMs) {
      void this.autoLogout();
      return;
    }
    if (elapsed >= this.warnAtMs) {
      const secondsLeft = Math.max(0, Math.ceil((this.timeoutMs - elapsed) / 1000));
      this.showWarning(secondsLeft);
      return;
    }
    // Sotto la soglia di avviso: se un alert è aperto (es. "Resta connesso" premuto in un altro
    // tab ha scritto un timestamp recente) va chiuso — multi-tab (FR-010).
    this.dismissWarning();
  }

  // Attività "di processo" (es. collo creato da pesata, issue #3591): l'operatore lavora senza toccare la UI.
  // Riparte il conteggio come "Resta connesso": bypassa il throttle e chiude l'eventuale avviso.
  // No-op se il tracking non è attivo o il logout è già in corso.
  registerAutomaticActivity(): void {
    if (!this.intervalId || this.loggingOut) {
      return;
    }
    this.touch(true);
  }

  // Sorgente di tempo wall-clock condivisa tra tab (NON performance.now, che è per-documento). Iniettabile per i test con clock manuale.
  private now: () => number = () => Date.now();

  // Ultimo istante di attività da localStorage. Self-healing: se la chiave è assente scrive now() alla prima lettura e lo restituisce (evita elapsed indefinito quando la chiave manca).
  private lastActivity(): number {
    const raw = localStorage.getItem(this.ACTIVITY_KEY);
    if (raw === null) {
      const timestamp = this.now();
      localStorage.setItem(this.ACTIVITY_KEY, String(timestamp));
      return timestamp;
    }
    const parsed = Number(raw);
    return Number.isNaN(parsed) ? this.now() : parsed;
  }

  // Lettura pura dell'ultima attività: a differenza di lastActivity() NON scrive nulla e distingue
  // "chiave assente o illeggibile" (null) da un valore. Serve a expireRestoredSession(): senza
  // alcun riferimento (sessione precedente alla feature, storage ripulito) non si può dire che la
  // sessione sia scaduta, e il self-healing di lastActivity() la ripartirebbe da adesso.
  private storedLastActivity(): number | null {
    const raw = localStorage.getItem(this.ACTIVITY_KEY);
    if (raw === null) {
      return null;
    }
    const parsed = Number(raw);
    return Number.isNaN(parsed) ? null : parsed;
  }

  // Gestore attività: ignorato durante l'avviso o il logout (solo il bottone conferma la presenza, FR-006); altrimenti aggiorna il timestamp (throttled).
  private onActivity = (): void => {
    if (this.isWarning() || this.loggingOut) {
      return;
    }
    this.touch();
  };

  // Scrittura throttled del timestamp. force=true bypassa il throttle e chiude l'eventuale avviso ("Resta connesso").
  private touch(force = false): void {
    const timestamp = this.now();
    if (!force && timestamp - this.lastWrite < this.THROTTLE_MS) {
      return;
    }
    this.lastWrite = timestamp;
    localStorage.setItem(this.ACTIVITY_KEY, String(timestamp));
    if (force) {
      this.dismissWarning();
    }
  }

  private isWarning(): boolean {
    return !!this.alert || this.warningPending;
  }

  private registerListeners(): void {
    this.ngZone.runOutsideAngular(() => {
      for (const eventName of this.ACTIVITY_EVENTS) {
        document.addEventListener(eventName, this.onActivity, {capture: true, passive: true});
      }
    });
  }

  private removeListeners(): void {
    for (const eventName of this.ACTIVITY_EVENTS) {
      document.removeEventListener(eventName, this.onActivity, {capture: true});
    }
  }

  // Preavviso con countdown. Se l'alert esiste aggiorna solo il messaggio; altrimenti lo crea una sola volta (warningPending evita creazioni concorrenti sui tick successivi).
  private showWarning(secondsLeft: number): void {
    const message = `Verrai disconnesso tra ${secondsLeft} secondi per inattività.`;
    if (this.alert) {
      this.updateAlertMessage(message);
      return;
    }
    if (this.warningPending) {
      return;
    }
    this.warningPending = true;
    void this.ngZone.run(() => this.presentWarning(message));
  }

  private async presentWarning(message: string): Promise<void> {
    const alert = await this.alertController.create({
      header: 'Sessione in scadenza',
      message,
      backdropDismiss: false,
      buttons: [{text: 'Resta connesso', handler: () => this.touch(true)}]
    });
    // Se nel frattempo il servizio è stato fermato o è iniziato il logout, non presentare l'alert.
    if (!this.intervalId || this.loggingOut) {
      this.warningPending = false;
      await alert.dismiss();
      return;
    }
    this.alert = alert;
    this.warningPending = false;
    await alert.present();
  }

  // Aggiorna il messaggio dell'alert; fallback sul nodo .alert-message se il re-render non avviene.
  private updateAlertMessage(message: string): void {
    if (!this.alert) {
      return;
    }
    this.alert.message = message;
    // Scoped sul proprio alert: un querySelector globale potrebbe aggiornare un altro ion-alert aperto.
    const node = this.alert.querySelector('.alert-message');
    if (node) {
      node.textContent = message;
    }
  }

  private dismissWarning(): void {
    this.warningPending = false;
    const alert = this.alert;
    this.alert = undefined;
    if (alert) {
      void alert.dismiss();
    }
  }

  // Terminazione della sessione: spegne eventuali risorse attive (es. fulmine bilancia in
  // spedizioneManualeBase), revoca server-side (best effort, il token può essere già revocato da
  // un altro tab), poi logout locale e redirect forzato a login.
  private async autoLogout(): Promise<void> {
    if (this.loggingOut) {
      return;
    }
    this.loggingOut = true;
    this.dismissWarning();
    try {
      // Va invocato PRIMA di revokeToken()/logout(): oltre a servire ancora il JWT per l'unset
      // server-side, azzera lo stato "risorsa attiva" così che il CanDeactivate della sezione
      // (valutato dal Router quando sotto viene forzato il redirect via hash) trovi già tutto
      // spento e non apra un alert di conferma che l'utente, assente, non potrebbe mai chiudere
      // (bug: redirect a login bloccato ma utente già sloggato).
      // Si attende l'esito dell'unset — al massimo 5 s e un retry, vedi forceDeactivate(): se
      // fallisce, la riga bilancia→OdS resta sul server e il logout non può più rimediare.
      await this.spedizioneManualeDeactivate.forceDeactivate();
    } catch (err) {
      // best effort: non deve impedire il logout anche se lo spegnimento risorsa fallisce.
      console.error('[InactivityLogout] forceDeactivate risorsa fallito', err);
    }
    let revokeTimeoutId: ReturnType<typeof setTimeout> | undefined;
    try {
      // Timeout di 5 s sulla revoca: una rete bloccata non deve impedire il logout locale.
      await Promise.race([
        this.authService.revokeToken(),
        new Promise((_, reject) => {
          revokeTimeoutId = setTimeout(() => reject(new Error('revoke-token timeout')), this.REVOKE_TIMEOUT_MS);
        })
      ]);
    } catch {
      // best effort: la disconnessione locale deve completarsi comunque (FR-008).
    } finally {
      clearTimeout(revokeTimeoutId);
    }
    await this.ngZone.run(async () => {
      // Il redirect via hash non chiude le modali Ionic (non sono legate al routing): senza
      // questo, l'utente assente le ritroverebbe aperte sopra la pagina di login al rientro.
      await this.dismissAllModals();
      this.stop();
      this.authService.logout();
      // Redirect via hash (pattern error.interceptor.ts): bypassa deliberatamente i guard
      // CanDeactivate — per definizione di inattività l'utente è assente e un dialog resterebbe
      // appeso bloccando il logout.
      window.location.hash = '#/account/login';
    });
  }

  // Chiude tutte le modali Ionic aperte (anche impilate). Best effort: un fallimento nel dismiss
  // non deve impedire logout e redirect.
  private async dismissAllModals(): Promise<void> {
    try {
      let modal = await this.modalController.getTop();
      while (modal) {
        await modal.dismiss(undefined, 'auto-logout');
        modal = await this.modalController.getTop();
      }
    } catch (err) {
      console.error('[InactivityLogout] chiusura modali fallita', err);
    }
  }
}
